import { useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  Input, Button, Card, CardHeader, CardTitle, CardBody,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import type { ContractFormValues } from '../../contract-form';
import type { ContractServiceConfigOption } from '@/api/contracts';
import {
  TYPE_TO_WAREHOUSE_TYPES, TYPE_TO_CAPACITY_SPEC,
  capacitySpec, modelsForCategories, useVbmModels,
} from './warehouse-models';
import { NumericInput } from '@/components/numeric-input';

const UNLIMITED = 999999999;

/**
 * Row for `type = 'warehouse'` — disk or memory config options that support
 * add (clone) and remove operations. Fields: model, brand (read-only),
 * quantity (commitment_value), price-per-unit, capacity_value, capacity_unit.
 * Model catalog is queried LIVE from the warehouse service (see warehouse-models).
 */
export function ServerVbmWarehouseRow({
  optsPath,
  rowIndex,
  isDefault,
  onInsert,
  onRemove,
}: {
  optsPath: string;
  rowIndex: number;
  /** Default-scope rows cannot be removed, only cloned. */
  isDefault: boolean;
  onInsert: (afterIndex: number) => void;
  onRemove: (index: number) => void;
}) {
  const { control, setValue, getValues } = useFormContext<ContractFormValues>();
  const rowPath = `${optsPath}.${rowIndex}` as never;

  const { models, manufactureOptions } = useVbmModels();

  const nameValue = (useWatch({ control, name: `${rowPath}.name` as never }) as string) ?? '';
  const allOpts = (useWatch({ control, name: optsPath as never }) as ContractServiceConfigOption[]) ?? [];
  const currentModelName = useWatch({ control, name: `${rowPath}.model_name` as never }) as string | undefined;

  /** Max capacity units allowed for this option name (from the default-scope row). */
  const defaultMax = useMemo(() => {
    const defaultRow = allOpts.find((o) => o.name === nameValue && (o as Record<string, unknown>).scope === 'default');
    const v = Number((defaultRow as Record<string, unknown> | undefined)?.max ?? UNLIMITED);
    return Number.isFinite(v) ? v : UNLIMITED;
  }, [allOpts, nameValue]);

  /** Sum of capacity_value × commitment_value for all rows sharing the same name. */
  const totalUsed = useMemo(
    () =>
      allOpts.reduce((sum, o, i) => {
        if (o.name !== nameValue || i === rowIndex) return sum;
        return sum + Number(o.capacity_value ?? 0) * Number(o.commitment_value ?? 1);
      }, 0),
    [allOpts, nameValue, rowIndex],
  );

  const currentCapacity = Number(getValues(`${rowPath}.capacity_value` as never) ?? 0);
  const remaining = defaultMax === UNLIMITED ? UNLIMITED : defaultMax - totalUsed;

  const modelOptions = useMemo(() => {
    const allowedTypes = TYPE_TO_WAREHOUSE_TYPES[nameValue] ?? [];
    return modelsForCategories(models, allowedTypes)
      .filter((m) => {
        if (defaultMax === UNLIMITED) return true;
        const specVal = capacitySpec(m, TYPE_TO_CAPACITY_SPEC[nameValue]).value;
        return specVal <= remaining;
      })
      .map((m) => ({ label: m.name, value: m.name }));
  }, [models, nameValue, defaultMax, remaining]);

  const quantityOptions = useMemo(() => {
    if (!currentModelName || !currentCapacity) return [];
    const maxQty = remaining === UNLIMITED ? 10 : Math.max(0, Math.floor(remaining / currentCapacity));
    return Array.from({ length: maxQty }, (_, i) => ({
      label: String(i + 1),
      value: String(i + 1),
    }));
  }, [currentModelName, currentCapacity, remaining]);

  function handleModelChange(value: string) {
    if (!value) {
      setValue(`${rowPath}.capacity_value` as never, null as never, { shouldDirty: true });
      setValue(`${rowPath}.brand_name` as never, null as never, { shouldDirty: true });
      setValue(`${rowPath}.capacity_unit` as never, null as never, { shouldDirty: true });
      return;
    }
    const model = models.find((m) => m.name === value);
    const spec = capacitySpec(model, TYPE_TO_CAPACITY_SPEC[nameValue]);
    setValue(`${rowPath}.capacity_value` as never, spec.value as never, { shouldDirty: true });
    setValue(`${rowPath}.brand_name` as never, (model?.brand ?? '') as never, { shouldDirty: true });
    setValue(`${rowPath}.capacity_unit` as never, spec.unit as never, { shouldDirty: true });
  }

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gap: 'var(--vn-space-3)',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
  };

  return (
    <Card style={{ marginBottom: 'var(--vn-space-2)' }}>
      {/* Row header with add / remove actions */}
      <CardHeader
        style={{ padding: 'var(--vn-space-2) var(--vn-space-3)' }}
        actions={
          <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
            <Button size="sm" variant="ghost" onClick={() => onInsert(rowIndex)}>
              + Add
            </Button>
            {!isDefault && (
              <Button size="sm" variant="ghost" onClick={() => onRemove(rowIndex)}>
                Remove
              </Button>
            )}
          </div>
        }
      >
        <CardTitle style={{ textTransform: 'uppercase' }}>{nameValue}</CardTitle>
      </CardHeader>

      {/* Fields */}
      <CardBody>
        <div style={gridStyle}>
          {/* Model — cascades to brand + capacity */}
          <Controller
            control={control}
            name={`${rowPath}.model_name` as never}
            render={({ field }) => (
              <Field label="Model">
                {(p) => (
                  <Select
                    value={(field.value as string) || ''}
                    onValueChange={(v) => { field.onChange(v); handleModelChange(v); }}
                  >
                    <SelectTrigger {...p} onBlur={field.onBlur}>
                      <SelectValue placeholder="Select model" />
                    </SelectTrigger>
                    <SelectContent>
                      {modelOptions.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            )}
          />
          {/* Brand — auto-filled from model, read-only */}
          <Controller
            control={control}
            name={`${rowPath}.brand_name` as never}
            render={({ field }) => (
              <Field label="Manufacture">
                {(p) => (
                  <Select value={(field.value as string) || ''} onValueChange={field.onChange} disabled>
                    <SelectTrigger {...p} onBlur={field.onBlur}>
                      <SelectValue placeholder="Auto" />
                    </SelectTrigger>
                    <SelectContent>
                      {manufactureOptions.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            )}
          />
          {/* Quantity (commitment_value) — derived from remaining quota */}
          <Controller
            control={control}
            name={`${rowPath}.commitment_value` as never}
            render={({ field }) => (
              <Field label="Quantity">
                {(p) => (
                  <Select
                    value={field.value != null ? String(field.value) : ''}
                    onValueChange={(v) => field.onChange(Number(v))}
                    disabled={!currentModelName}
                  >
                    <SelectTrigger {...p} onBlur={field.onBlur}>
                      <SelectValue placeholder="Qty" />
                    </SelectTrigger>
                    <SelectContent>
                      {quantityOptions.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            )}
          />
          {/* Price per unit (commitment_price_per_unit) */}
          <Controller
            control={control}
            name={`${rowPath}.commitment_price_per_unit` as never}
            render={({ field }) => (
              <Field label="Price/unit">
                {(p) => (
                  <NumericInput
                    {...p}
                    value={field.value == null ? '' : String(field.value)}
                    onChange={(e) => field.onChange(e.target.value === '' ? undefined : Number(e.target.value))}
                    onBlur={field.onBlur}
                    name={field.name}
                    ref={field.ref}
                  />
                )}
              </Field>
            )}
          />
          {/* Capacity value — auto-filled from model spec, read-only */}
          <Controller
            control={control}
            name={`${rowPath}.capacity_value` as never}
            render={({ field }) => (
              <Field label="Capacity value">
                {(p) => (
                  <NumericInput
                    {...p}
                    disabled
                    value={field.value == null ? '' : String(field.value)}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                    ref={field.ref}
                  />
                )}
              </Field>
            )}
          />
          {/* Capacity unit — auto-filled from model spec, read-only */}
          <Controller
            control={control}
            name={`${rowPath}.capacity_unit` as never}
            render={({ field }) => (
              <Field label="Unit">
                {(p) => (
                  <Input
                    {...p}
                    type="text"
                    disabled
                    value={(field.value as string) ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                    ref={field.ref}
                  />
                )}
              </Field>
            )}
          />
        </div>
      </CardBody>
    </Card>
  );
}
